import { Modal, message } from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { useAuth } from '../../contexts/Auth'
import { PropertyDataMapper } from '../../services/property/dataMapper'
import { FeaturesService } from '../../services/property/features.service'
import { PropertyService } from '../../services/property/property.service'
import MixpanelTracker from '../../services/track/mixpanel'
import PropertySubmissionSuccess from './PropertySubmissionSuccess'
import Step1 from './Step1'
import Step1_1 from './Step1-1'
import Step1_2 from './Step1-2'
import Step2 from './Step2'
import Step3 from './Step3'
import Step4 from './Step4'
import Stepper from './Stepper'

const STORAGE_PREFIX = process.env.REACT_APP_STORAGE_PREFIX || 'haup-submission'

const generateRandomId = () => Math.floor(10000 + Math.random() * 90000).toString()

const mobileButtonStyles = `
  .completion-modal-button {
    padding: 12px 24px;
  }

  @media (max-width: 768px) {
    .completion-modal-button {
      padding: 12px 20px;
    }
  }
`

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style')
  styleSheet.textContent = mobileButtonStyles
  document.head.appendChild(styleSheet)
}

interface DocumentFormData {
  idCard: string | null
  houseRegistration: string | null
  condoOwnership: string | null
  condoJuristic: string | null
  bankBook: string | null
}

interface PropertyFormData {
  propertyType: string
  unitSize: string
  bedrooms: string
  bathrooms: string
  floorNumber: string
  roomNumber: string
  roomPosition: string
  hasBalcony: boolean | null
  isPetFriendly: boolean | null
  maxGuests: string
  availability: string
  availableDate: any
  propertyPhotos: string[]
  videoTour: string | null
  floorPlan: string | null
  minRentalDuration: '1month' | '3-6months' | '1year' | null
  preferredRentalDuration: string
  monthlyPrice: string
  sixMonthPrice: string
  yearlyPrice: string
  securityDeposit: string
  useHaupAgent: boolean
  agentFee: string
  documents: DocumentFormData
  listingTitle: string
  shortDescription: string
  uniqueSellingPoints: string[]
  nearbyHighlights: string[]
  noiseLevel: string
  viewType: string[]
  amenities: string[]
  buildingFacilities: string[]
  locationName: string
  location: string
  locationLat: number | null
  locationLng: number | null
  nearbyBTSMRT: Array<{
    id: string
    stationId: string
    stationName: string
    walkingMinutes: string
  }>
  nearbyPopularLocations: Array<{
    id: string
    locationName: string
    walkingMinutes: string
  }>
}

interface StepProgress {
  step1: number
  step2: number
  step3: number
  step4: number
}

interface StepTutorialProps {
  step: number
  title: string
  description: string
  icon: React.ReactNode
  onNext: () => void
  onBack?: () => void
}

const StepTutorial: React.FC<StepTutorialProps> = ({
  step,
  title,
  description,
  icon,
  onNext,
  onBack,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [iconVisible, setIconVisible] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true)
    }, 100)

    const iconTimer = setTimeout(() => {
      setIconVisible(true)
    }, 300)

    return () => {
      clearTimeout(timer)
      clearTimeout(iconTimer)
    }
  }, [])

  return (
    <div
      style={{
        backgroundColor: '#fff',
        padding: '24px 16px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '500px',
        margin: '0 auto',
        boxSizing: 'border-box',
        fontFamily: 'Poppins, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        minHeight: '100vh',
        transition:
          'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
      }}
    >
      <div
        style={{
          fontSize: '80px',
          color: '#4C4848',
          marginBottom: '24px',
          transition:
            'opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1), transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: iconVisible ? 1 : 0,
          transform: iconVisible ? 'scale(1) rotate(0deg)' : 'scale(0.8) rotate(-5deg)',
          animation: iconVisible ? 'float 3s ease-in-out infinite' : 'none',
        }}
      >
        {icon}
      </div>

      <style>
        {`
          @keyframes float {
            0%, 100% {
              transform: scale(1) rotate(0deg) translateY(0px);
            }
            50% {
              transform: scale(1) rotate(0deg) translateY(-8px);
            }
          }
        `}
      </style>

      <div
        style={{
          marginBottom: '16px',
          transition:
            'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          transitionDelay: '0.2s',
        }}
      >
        <p
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#667085',
            textTransform: 'uppercase',
            margin: '0',
          }}
        >
          Step {step}
        </p>
        <h2
          style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#212427',
            margin: '8px 0 0',
          }}
        >
          {title}
        </h2>
      </div>
      <p
        style={{
          fontSize: '16px',
          lineHeight: '1.5',
          color: '#667085',
          textAlign: 'left',
          marginBottom: '32px',
          transition:
            'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          transitionDelay: '0.4s',
        }}
      >
        {description}
      </p>

      <div
        style={{
          display: 'flex',
          justifyContent: onBack ? 'space-between' : 'flex-end',
          width: '100%',
          marginTop: '30px',
          gap: '16px',
          transition:
            'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          transitionDelay: '0.6s',
        }}
      >
        {onBack && (
          <button
            onClick={onBack}
            style={{
              flex: '1',
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: '#667085',
              border: '1px solid #D6DAE0',
              borderRadius: '24px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '16px',
              fontFamily: 'Poppins, sans-serif',
              transition: 'all 0.2s ease',
              transform: 'scale(1)',
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.95)'
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            Back
          </button>
        )}

        <button
          onClick={onNext}
          style={{
            flex: '1',
            padding: '12px 24px',
            backgroundColor: '#4C4848',
            color: 'white',
            border: 'none',
            borderRadius: '24px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '16px',
            fontFamily: 'Poppins, sans-serif',
            transition: 'all 0.2s ease',
            transform: 'scale(1)',
            boxShadow: '0 2px 8px rgba(183, 135, 90, 0.3)',
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = 'scale(0.95)'
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'scale(1)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)'
          }}
        >
          Next
        </button>
      </div>
    </div>
  )
}

const tutorials = {
  1: {
    title: 'Tell us about your property',
    description:
      "Share the essential information about your property, including its location, available amenities, and size. These details help guests quickly understand what you're offering and decide if it's the right fit for their stay.",
    icon: (
      <img
        src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/3b15TjMmNfKf37L5fYwdCEtsiF8MfL8rLyyipL1az6rMyQeuzKGfPu2Bfqpx4kojn37DNicngoASoEesMNLhZFsd.png"
        alt="Step 1 Icon"
        style={{ width: '150px', height: '150px' }}
      />
    ),
  },
  2: {
    title: 'Make your property stand out',
    description:
      'Highlight your property unique and upload photos of the property. These details help your listing stand out and inspire guests to book.',
    icon: (
      <img
        src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/3b15TjMmNfKf37L5fYwdCEtsiF8MfL8rLyyipL1az6rMyQeuzKGfPu2Bfqpx4kojn37DNicngoASoEesMNLhZFsd.png"
        alt="Step 2 Icon"
        style={{ width: '150px', height: '150px' }}
      />
    ),
  },
  3: {
    title: 'Set your price and earnings',
    description:
      'Set your monthly rent and choose the lease durations you want to offer. HAUP will help estimate your potential earnings so you can see how much you could make.',
    icon: (
      <img
        src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/51tGQZ8UiqjbYXTgXPhyC5MSHPh6j6WgApSN97zsRB79gtwxAozEsFw25EqUZyrf4CpiPbomjfxWQ7dtF4CymXLF.png"
        alt="Step 3 Icon"
        style={{ width: '150px', height: '150px' }}
      />
    ),
  },
  4: {
    title: 'Upload documents',
    description:
      "You'll be asked to upload important documents, such as your house registration book and condo ownership. These help verify your listing and build trust with renters. You can skip this step for now, but completing it early ensures a smoother approval process and faster listing activation.",
    icon: (
      <img
        src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/1KyatujG6VKojwLvasUqSvfBWyRyVYwycumkpLv5q7DaqKKqWj5qhxJBictMUPvUD4sePsYnhzFH9xQ3heLYKLzk.png"
        alt="Step 4 Icon"
        style={{ width: '150px', height: '150px' }}
      />
    ),
  },
}

const PropertySubmissionPage: React.FC = () => {
  const { path } = useRouteMatch()
  const history = useHistory()
  const location = useLocation()
  const { userLoggedIn } = useAuth()
  const isInitialMount = useRef(true)

  const [currentStep, setCurrentStep] = useState(1)
  const [showCompletionModal, setShowCompletionModal] = useState(false)
  const [draftId, setDraftId] = useState<number | null>(null)
  const [isSubmittingDraft, setIsSubmittingDraft] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [isLoadingProperty, setIsLoadingProperty] = useState(false)
  const [currentId, setCurrentId] = useState<string>('')
  const [tutorialShown, setTutorialShown] = useState<{ [key: number]: boolean }>({})

  useEffect(() => {
    if (!currentId) return
    try {
      const storageKey = `${STORAGE_PREFIX}_property_tutorialShown_${currentId}`
      sessionStorage.setItem(storageKey, JSON.stringify(tutorialShown))
    } catch (e) {
      console.error('Failed to save tutorialShown to sessionStorage', e)
    }
  }, [tutorialShown, currentId])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const urlDraftId = urlParams.get('draft_id')
    let id = urlDraftId
    if (!id) {
      const storageKey = `${STORAGE_PREFIX}_property_temp_id`
      let storedTempId = sessionStorage.getItem(storageKey)
      if (!storedTempId) {
        storedTempId = generateRandomId()
        sessionStorage.setItem(storageKey, storedTempId)
      }
      id = storedTempId
    }
    setCurrentId(id)

    try {
      const storageKey = `${STORAGE_PREFIX}_property_tutorialShown_${id}`
      const storedState = sessionStorage.getItem(storageKey)
      setTutorialShown(
        storedState ? JSON.parse(storedState) : { 1: false, 2: false, 3: false, 4: false },
      )
    } catch (e) {
      console.error('Failed to parse tutorialShown from sessionStorage', e)
      setTutorialShown({ 1: false, 2: false, 3: false, 4: false })
    }
  }, [location.search])

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const queryDraftId = urlParams.get('draft_id')
    if (queryDraftId) {
      setDraftId(parseInt(queryDraftId, 10))
      setCurrentId(queryDraftId)
    }
  }, [location.search])

  const fetchPropertyData = useCallback(
    async (propertyId: number) => {
      if (!userLoggedIn?.userToken) {
        return
      }

      setIsLoadingProperty(true)
      try {
        const response = await PropertyService.getPropertyDetail(propertyId, userLoggedIn.userToken)
        if (response.result && response.data) {
          const mappedFormData = await PropertyDataMapper.convertPropertyDetailToFormData(
            response.data,
            userLoggedIn.userToken,
          )
          setFormData((prevData) => ({ ...prevData, ...mappedFormData }))
          if (response.data.completed_steps) {
            const newProgress: StepProgress = { step1: 0, step2: 0, step3: 0, step4: 0 }
            for (const step of response.data.completed_steps) {
              if (step === 1) newProgress.step1 = 100
              if (step === 2) newProgress.step2 = 100
              if (step === 3) newProgress.step3 = 100
              if (step === 4) newProgress.step4 = 100
            }
            setStepProgress(newProgress)
          }
          console.log('Property data loaded and mapped to form:', mappedFormData)
        } else {
          console.error('Failed to fetch property data:', response.message)
          message.error('Failed to load property data. Please refresh and try again.')
        }
      } catch (error) {
        console.error('Error fetching property data:', error)
      } finally {
        setIsLoadingProperty(false)
      }
    },
    [userLoggedIn],
  )

  useEffect(() => {
    if (isInitialMount.current) {
      if (draftId && userLoggedIn?.userToken) {
        fetchPropertyData(draftId)
        isInitialMount.current = false
      }
    }
  }, [draftId, userLoggedIn, fetchPropertyData])

  const [formData, setFormData] = useState<PropertyFormData>({
    propertyType: '',
    unitSize: '',
    bedrooms: '',
    bathrooms: '',
    floorNumber: '',
    roomNumber: '',
    roomPosition: '',
    hasBalcony: null,
    isPetFriendly: null,
    maxGuests: '',
    availability: '',
    availableDate: null,
    propertyPhotos: [],
    videoTour: null,
    floorPlan: null,
    minRentalDuration: '1year',
    preferredRentalDuration: '',
    monthlyPrice: '',
    sixMonthPrice: '',
    yearlyPrice: '',
    securityDeposit: '',
    useHaupAgent: false,
    agentFee: '',
    documents: {
      idCard: null,
      houseRegistration: null,
      condoOwnership: null,
      condoJuristic: null,
      bankBook: null,
    },
    listingTitle: '',
    shortDescription: '',
    uniqueSellingPoints: [],
    nearbyHighlights: [],
    noiseLevel: '',
    viewType: [],
    amenities: [],
    buildingFacilities: [],
    locationName: '',
    location: '',
    locationLat: null,
    locationLng: null,
    nearbyBTSMRT: [{ id: 'default-1', stationId: '', stationName: '', walkingMinutes: '' }],
    nearbyPopularLocations: [{ id: 'default-2', locationName: '', walkingMinutes: '' }],
  })

  const [stepProgress, setStepProgress] = useState<StepProgress>({
    step1: 0,
    step2: 0,
    step3: 0,
    step4: 0,
  })

  useEffect(() => {
    const pathSegments = location.pathname.split('/')
    const stepSegment = pathSegments.find((segment) => segment.startsWith('step'))
    let stepNumber = 1

    if (stepSegment) {
      if (stepSegment === 'step1-1' || stepSegment === 'step1-2') {
        stepNumber = 1
      } else {
        stepNumber = parseInt(stepSegment.replace('step', ''))
      }
    }

    if (!isNaN(stepNumber)) {
      setCurrentStep(stepNumber)
    }

    MixpanelTracker.track('Property Submission Page Viewed', {
      'Current Step': stepNumber,
      'Sub Step':
        stepSegment === 'step1-1'
          ? '1-1'
          : stepSegment === 'step1-2'
          ? '1-2'
          : stepNumber.toString(),
    })
  }, [location.pathname, location.search])

  useEffect(() => {
    if (currentStep > 1) {
      setTutorialShown((prev) => {
        const newState = { ...prev }
        for (let i = 1; i < currentStep; i++) {
          newState[i] = true
        }
        return newState
      })
    }
  }, [currentStep])

  const handleTutorialNext = useCallback((stepNumber: number) => {
    setTutorialShown((prev) => ({
      ...prev,
      [stepNumber]: true,
    }))
    MixpanelTracker.track('Property Submission Tutorial Completed', {
      Step: stepNumber,
    })
  }, [])

  const handlePrevious = useCallback(() => {
    const urlParams = new URLSearchParams(location.search)
    const continueParam = urlParams.get('continue')

    if (continueParam === 'true') {
      history.push('/submission#property')
      return
    }

    const pathSegments = location.pathname.split('/')
    const stepSegment = pathSegments.find((segment) => segment.startsWith('step'))
    const draftQuery = draftId ? `?draft_id=${draftId}` : ''

    if (stepSegment === 'step1-1') {
      history.push(`/property-submission/step1${draftQuery}`)
    } else if (stepSegment === 'step1-2') {
      history.push(`/property-submission/step1-1${draftQuery}`)
    } else if (currentStep > 1) {
      const prevStep = currentStep - 1
      const baseUrl =
        prevStep === 1 ? '/property-submission' : `/property-submission/step${prevStep}`
      history.push(`${baseUrl}${draftQuery}`)
    } else {
      history.push('/submission')
    }
  }, [currentStep, history, location.pathname, location.search, draftId])

  const handleTutorialBack = useCallback(
    (stepNumber: number) => {
      setTutorialShown((prev) => ({ ...prev, [stepNumber]: false }))
      handlePrevious()
    },
    [handlePrevious],
  )

  const updateFormData = useCallback((stepData: Partial<PropertyFormData>) => {
    setFormData((prev) => ({ ...prev, ...stepData }))
  }, [])

  const createDraft = useCallback(async () => {
    if (!userLoggedIn?.userToken) {
      console.error('User token is missing')
      return null
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_TS_RESTFUL_API_URL}/property/submit`, {
        method: 'POST',
        headers: {
          Authorization: userLoggedIn.userToken,
          project: 'haup-stable',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      })

      if (!response.ok) {
        throw new Error('Failed to create draft')
      }

      const result = await response.json()
      if (result.result && result.data?.id) {
        return result.data.id
      } else {
        throw new Error(result.message || 'Failed to create draft')
      }
    } catch (error) {
      console.error('Error creating draft:', error)
      return null
    }
  }, [userLoggedIn])

  const getPropertyTypeId = (propertyTypeName: string): number => {
    const propertyTypeMap: { [key: string]: number } = {
      condo: 1,
      apartment: 2,
      house: 3,
      townhouse: 4,
    }
    return propertyTypeMap[propertyTypeName.toLowerCase()] || 1
  }

  const saveStep1 = useCallback(
    async (draftIdToSave: number) => {
      if (!userLoggedIn?.userToken) {
        console.error('User token is missing')
        return false
      }

      const payload = {
        property_type_id: getPropertyTypeId(formData.propertyType),
        name: formData.listingTitle || '',
        description: formData.shortDescription || '',
        unit_size: formData.unitSize,
        bedrooms: parseInt(formData.bedrooms.match(/\d+/)?.[0] || '0', 10),
        bathrooms: parseInt(formData.bathrooms.match(/\d+/)?.[0] || '0', 10),
        floor_number: parseInt(formData.floorNumber, 10),
        room_number: formData.roomNumber,
        room_position: formData.roomPosition,
        has_balcony: formData.hasBalcony,
        is_pet_friendly: formData.isPetFriendly,
        max_guests: parseInt(formData.maxGuests, 10),
        noise_level: formData.noiseLevel,
        availability_settings: {
          type: formData.availability === 'Available immediately' ? 'immediate' : 'after_date',
          available_date: formData.availableDate
            ? formData.availableDate.format('YYYY-MM-DD')
            : undefined,
        },
        location: {
          place_name: formData.locationName,
          address: formData.location,
          lat: formData.locationLat,
          lng: formData.locationLng,
          district: '',
          province: '',
          postal_code: '',
        },
        nearby_transport:
          formData.nearbyBTSMRT
            ?.map((item) => ({
              station_id: parseInt(item.stationId.split(',')[0] || '0', 10),
              walking_minutes: parseInt(item.walkingMinutes, 10),
            }))
            .filter((item) => item.station_id > 0) || [],
        selected_features: await FeaturesService.getSelectedFeatureIds(
          {
            nearbyHighlights: formData.nearbyHighlights || [],
            viewType: formData.viewType || [],
            amenities: formData.amenities || [],
            buildingFacilities: formData.buildingFacilities || [],
            uniqueSellingPoints: formData.uniqueSellingPoints || [],
          },
          userLoggedIn.userToken,
        ),
        nearby_locations:
          formData.nearbyPopularLocations
            ?.map((item) => ({
              location_name: item.locationName,
              address: item.locationName,
              lat: null,
              lng: null,
              walking_minutes: parseInt(item.walkingMinutes, 10),
            }))
            .filter((item) => item.location_name) || [],
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_TS_RESTFUL_API_URL}/property/draft/${draftIdToSave}/step/1`,
          {
            method: 'PUT',
            headers: {
              Authorization: userLoggedIn.userToken,
              project: 'haup-stable',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          },
        )

        if (!response.ok) {
          throw new Error('Failed to save step 1')
        }

        const result = await response.json()
        return result.result === true
      } catch (error) {
        console.error('Error saving step 1:', error)
        return false
      }
    },
    [userLoggedIn, formData],
  )

  const saveStep2 = useCallback(
    async (draftIdToSave: number) => {
      if (!userLoggedIn?.userToken) {
        console.error('User token is missing')
        return false
      }

      const gallery = []
      let sequence = 1

      formData.propertyPhotos?.forEach((url) => {
        gallery.push({
          url,
          type: 'image',
          sequence: sequence++,
          title: `Property Photo ${sequence - 1}`,
        })
      })

      if (formData.videoTour) {
        gallery.push({
          url: formData.videoTour,
          type: 'video',
          sequence: sequence++,
          title: 'Virtual Tour',
        })
      }

      if (formData.floorPlan) {
        gallery.push({
          url: formData.floorPlan,
          type: 'floor_plan',
          sequence: sequence++,
          title: 'Floor Plan',
        })
      }

      const payload = { gallery }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_TS_RESTFUL_API_URL}/property/draft/${draftIdToSave}/step/2`,
          {
            method: 'PUT',
            headers: {
              Authorization: userLoggedIn.userToken,
              project: 'haup-stable',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          },
        )

        if (!response.ok) {
          throw new Error('Failed to save step 2')
        }

        const result = await response.json()
        return result.result === true
      } catch (error) {
        console.error('Error saving step 2:', error)
        return false
      }
    },
    [userLoggedIn, formData],
  )

  const saveStep3 = useCallback(
    async (draftIdToSave: number) => {
      if (!userLoggedIn?.userToken) {
        console.error('User token is missing')
        return false
      }

      const mapMinRentalDuration = (duration: string | null) => {
        switch (duration) {
          case '1month':
            return 'MONTHLY'
          case '3-6months':
            return 'THREE_TO_SIX_MONTHS'
          case '1year':
            return 'YEARLY'
          default:
            return 'MONTHLY'
        }
      }

      const pricingOptions = []

      if (formData.minRentalDuration === '1month') {
        if (formData.monthlyPrice) {
          pricingOptions.push({
            duration_type: 'MONTHLY',
            monthly_price: parseInt(formData.monthlyPrice, 10),
            is_available: true,
          })
        }
        if (formData.sixMonthPrice) {
          pricingOptions.push({
            duration_type: 'THREE_TO_SIX_MONTHS',
            monthly_price: parseInt(formData.sixMonthPrice, 10),
            is_available: true,
          })
        }
        if (formData.yearlyPrice) {
          pricingOptions.push({
            duration_type: 'YEARLY',
            monthly_price: parseInt(formData.yearlyPrice, 10),
            is_available: true,
          })
        }
      } else if (formData.minRentalDuration === '3-6months') {
        if (formData.sixMonthPrice) {
          pricingOptions.push({
            duration_type: 'THREE_TO_SIX_MONTHS',
            monthly_price: parseInt(formData.sixMonthPrice, 10),
            is_available: true,
          })
        }
        if (formData.yearlyPrice) {
          pricingOptions.push({
            duration_type: 'YEARLY',
            monthly_price: parseInt(formData.yearlyPrice, 10),
            is_available: true,
          })
        }
      } else if (formData.minRentalDuration === '1year') {
        if (formData.yearlyPrice) {
          pricingOptions.push({
            duration_type: 'YEARLY',
            monthly_price: parseInt(formData.yearlyPrice, 10),
            is_available: true,
          })
        }
      }

      const payload = {
        minimum_rental_duration: mapMinRentalDuration(formData.minRentalDuration),
        pricing_options: pricingOptions,
        security_deposit: parseInt(formData.securityDeposit, 10),
        use_haup_as_agent: formData.useHaupAgent,
        custom_agent_fee: formData.useHaupAgent ? parseInt(formData.agentFee || '0', 10) : 0,
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_TS_RESTFUL_API_URL}/property/draft/${draftIdToSave}/step/3`,
          {
            method: 'PUT',
            headers: {
              Authorization: userLoggedIn.userToken,
              project: 'haup-stable',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          },
        )

        if (!response.ok) {
          throw new Error('Failed to save step 3')
        }

        const result = await response.json()
        return result.result === true
      } catch (error) {
        console.error('Error saving step 3:', error)
        return false
      }
    },
    [userLoggedIn, formData],
  )

  const saveStep4 = useCallback(
    async (draftIdToSave: number) => {
      if (!userLoggedIn?.userToken) {
        console.error('User token is missing')
        return false
      }

      const documentTypeMap: { [key: string]: string } = {
        idCard: 'ID_CARD',
        houseRegistration: 'HOUSE_REGISTRATION',
        condoOwnership: 'OWNERSHIP_CERTIFICATE',
        bankBook: 'BANK_BOOK',
        condoJuristic: 'CONDO_JURISTIC_APPROVAL',
      }

      const documentNameMap: { [key: string]: string } = {
        idCard: 'National ID Card',
        houseRegistration: 'House Registration Book',
        condoOwnership: 'Condo Ownership Certificate',
        bankBook: 'Bank Account Book',
        condoJuristic: 'Juristic Person Approval',
      }

      const documents = []
      const formDocuments = formData.documents || {}

      for (const [fieldName, fileUrl] of Object.entries(formDocuments)) {
        if (fileUrl && documentTypeMap[fieldName]) {
          documents.push({
            document_type: documentTypeMap[fieldName],
            document_name: documentNameMap[fieldName],
            file_url: fileUrl,
            original_filename: `${fieldName}_upload.${
              fileUrl.toLowerCase().includes('.pdf') ? 'pdf' : 'jpg'
            }`,
          })
        }
      }

      const payload = { documents }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_TS_RESTFUL_API_URL}/property/draft/${draftIdToSave}/step/4`,
          {
            method: 'PUT',
            headers: {
              Authorization: userLoggedIn.userToken,
              project: 'haup-stable',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          },
        )

        if (!response.ok) {
          throw new Error('Failed to save step 4')
        }

        const result = await response.json()
        return result.result === true
      } catch (error) {
        console.error('Error saving step 4:', error)
        return false
      }
    },
    [userLoggedIn, formData],
  )

  const publishProperty = useCallback(
    async (propertyId: number) => {
      if (!userLoggedIn?.userToken) {
        console.error('User token is missing')
        return null
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_TS_RESTFUL_API_URL}/property/publish/${propertyId}`,
          {
            method: 'POST',
            headers: {
              Authorization: userLoggedIn.userToken,
              project: 'haup-stable',
              'Content-Type': 'application/json',
            },
          },
        )

        if (!response.ok) {
          throw new Error('Failed to publish property')
        }

        const result = await response.json()
        if (result.result === true && result.data) {
          return result.data
        } else {
          throw new Error(result.message || 'Failed to publish property')
        }
      } catch (error) {
        console.error('Error publishing property:', error)
        return null
      }
    },
    [userLoggedIn],
  )

  const unpublishProperty = useCallback(
    async (propertyId: number) => {
      if (!userLoggedIn?.userToken) {
        console.error('User token is missing')
        return null
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_TS_RESTFUL_API_URL}/property/unpublish/${propertyId}`,
          {
            method: 'POST',
            headers: {
              Authorization: userLoggedIn.userToken,
              project: 'haup-stable',
              'Content-Type': 'application/json',
            },
          },
        )

        if (!response.ok) {
          throw new Error('Failed to unpublish property')
        }

        const result = await response.json()
        if (result.result === true) {
          return true
        } else {
          throw new Error(result.message || 'Failed to unpublish property')
        }
      } catch (error) {
        console.error('Error unpublishing property:', error)
        return null
      }
    },
    [userLoggedIn],
  )

  const handleStepProgress = useCallback((step: number, progressPercentage: number) => {
    setStepProgress((prev) => ({
      ...prev,
      [`step${step}`]: progressPercentage,
    }))
  }, [])

  const calculateStep1BaseProgress = useCallback(() => {
    const fields = [
      formData.propertyType,
      formData.unitSize,
      formData.bedrooms,
      formData.bathrooms,
      formData.floorNumber,
      formData.roomNumber,
      formData.roomPosition,
      formData.hasBalcony !== null,
      formData.isPetFriendly !== null,
      formData.maxGuests,
      formData.availability,
      formData.availability === 'Available after this date' ? formData.availableDate : true,
    ]
    const filledFields = fields.filter((field) => field && field !== '').length
    return Math.round((filledFields / fields.length) * 100)
  }, [
    formData.propertyType,
    formData.unitSize,
    formData.bedrooms,
    formData.bathrooms,
    formData.floorNumber,
    formData.roomNumber,
    formData.roomPosition,
    formData.hasBalcony,
    formData.isPetFriendly,
    formData.maxGuests,
    formData.availability,
    formData.availableDate,
  ])

  const calculateStep1_1Progress = useCallback(() => {
    const fields = [
      formData.location,
      formData.nearbyBTSMRT && formData.nearbyBTSMRT.length > 0,
      formData.nearbyPopularLocations && formData.nearbyPopularLocations.length > 0,
    ]
    const filledFields = fields.filter((field) => field && field !== '').length
    return Math.round((filledFields / fields.length) * 100)
  }, [formData.location, formData.nearbyBTSMRT, formData.nearbyPopularLocations])

  const calculateStep1_2Progress = useCallback(() => {
    const fields = [
      formData.listingTitle,
      formData.shortDescription,
      formData.uniqueSellingPoints && formData.uniqueSellingPoints.length > 0,
      formData.nearbyHighlights && formData.nearbyHighlights.length > 0,
      formData.noiseLevel,
      formData.viewType && formData.viewType.length > 0,
      formData.amenities && formData.amenities.length > 0,
      formData.buildingFacilities && formData.buildingFacilities.length > 0,
    ]
    const filledFields = fields.filter((field) => field && field !== '').length
    return Math.round((filledFields / fields.length) * 100)
  }, [
    formData.listingTitle,
    formData.shortDescription,
    formData.uniqueSellingPoints,
    formData.nearbyHighlights,
    formData.noiseLevel,
    formData.viewType,
    formData.amenities,
    formData.buildingFacilities,
  ])

  const handleStep1Progress = useCallback(
    (progress: number) => {
      const step1_1Progress = calculateStep1_1Progress()
      const step1_2Progress = calculateStep1_2Progress()
      const step1BaseProgressPercentage = progress * 0.33
      const step1_1ProgressPercentage = step1_1Progress * 0.33
      const step1_2ProgressPercentage = step1_2Progress * 0.33
      const totalStep1Progress =
        step1BaseProgressPercentage + step1_1ProgressPercentage + step1_2ProgressPercentage
      handleStepProgress(1, Math.round(totalStep1Progress))
    },
    [handleStepProgress, calculateStep1_1Progress, calculateStep1_2Progress],
  )

  const handleStep1_1Progress = useCallback(
    (progress: number) => {
      const step1Progress = calculateStep1BaseProgress()
      const step1_2Progress = calculateStep1_2Progress()
      const step1BaseProgressPercentage = step1Progress * 0.33
      const step1_1ProgressPercentage = progress * 0.33
      const step1_2ProgressPercentage = step1_2Progress * 0.33
      const totalStep1Progress =
        step1BaseProgressPercentage + step1_1ProgressPercentage + step1_2ProgressPercentage
      handleStepProgress(1, Math.round(totalStep1Progress))
    },
    [handleStepProgress, calculateStep1BaseProgress, calculateStep1_2Progress],
  )

  const handleStep1_2Progress = useCallback(
    (progress: number) => {
      const step1Progress = calculateStep1BaseProgress()
      const step1_1Progress = calculateStep1_1Progress()
      const step1BaseProgressPercentage = step1Progress * 0.33
      const step1_1ProgressPercentage = step1_1Progress * 0.33
      const step1_2ProgressPercentage = progress * 0.33
      const totalStep1Progress =
        step1BaseProgressPercentage + step1_1ProgressPercentage + step1_2ProgressPercentage
      handleStepProgress(1, Math.round(totalStep1Progress))
    },
    [handleStepProgress, calculateStep1BaseProgress, calculateStep1_1Progress],
  )

  const handleStep2Progress = useCallback(
    (progress: number) => handleStepProgress(2, progress),
    [handleStepProgress],
  )

  const handleStep3Progress = useCallback(
    (progress: number) => handleStepProgress(3, progress),
    [handleStepProgress],
  )

  const handleStep4Progress = useCallback(
    (progress: number) => handleStepProgress(4, progress),
    [handleStepProgress],
  )

  const handleStepClick = useCallback(
    (stepNumber: number) => {
      let url: string

      if (stepNumber === 1) {
        url = '/property-submission'
      } else {
        url = `/property-submission/step${stepNumber}`
      }

      history.push(url)
    },
    [history],
  )

  const handleNext = useCallback(async () => {
    const pathSegments = location.pathname.split('/')
    const stepSegment = pathSegments.find((segment) => segment.startsWith('step'))

    if (currentStep === 1) {
      if (stepSegment === 'step1-1') {
        history.push('/property-submission/step1-2')
      } else if (stepSegment === 'step1-2') {
        setIsSubmittingDraft(true)

        try {
          let currentDraftId = draftId

          if (!currentDraftId) {
            currentDraftId = await createDraft()
            if (!currentDraftId) {
              console.error('Failed to create draft')
              message.error('Failed to create draft. Please try again.')
              setIsSubmittingDraft(false)
              return
            }
            setDraftId(currentDraftId)
          }

          const step1Saved = await saveStep1(currentDraftId)
          if (!step1Saved) {
            console.error('Failed to save step 1')
            message.error('Failed to save step 1. Please try again.')
            setIsSubmittingDraft(false)
            return
          }
          handleStepProgress(1, 100)
          history.push(`/property-submission/step2?draft_id=${currentDraftId}`)
        } catch (error) {
          console.error('Error in step 1-2 completion:', error)
          message.error('An error occurred while saving. Please try again.')
        } finally {
          setIsSubmittingDraft(false)
        }
      } else {
        history.push('/property-submission/step1-1')
      }
    } else if (currentStep === 2) {
      if (draftId) {
        setIsSubmittingDraft(true)

        try {
          const step2Saved = await saveStep2(draftId)
          if (!step2Saved) {
            console.error('Failed to save step 2')
            message.error('Failed to save step 2. Please try again.')
            setIsSubmittingDraft(false)
            return
          }
          handleStepProgress(2, 100)
          history.push(`/property-submission/step3?draft_id=${draftId}`)
        } catch (error) {
          console.error('Error in step 2 completion:', error)
          message.error('An error occurred while saving. Please try again.')
        } finally {
          setIsSubmittingDraft(false)
        }
      } else {
        history.push('/property-submission/step3')
      }
    } else if (currentStep === 3) {
      if (draftId) {
        setIsSubmittingDraft(true)

        try {
          const step3Saved = await saveStep3(draftId)
          if (!step3Saved) {
            console.error('Failed to save step 3')
            message.error('Failed to save step 3. Please try again.')
            setIsSubmittingDraft(false)
            return
          }
          handleStepProgress(3, 100)
          setShowCompletionModal(true)
        } catch (error) {
          console.error('Error in step 3 completion:', error)
          message.error('An error occurred while saving. Please try again.')
        } finally {
          setIsSubmittingDraft(false)
        }
      } else {
        setShowCompletionModal(true)
      }
    } else if (currentStep === 4) {
      if (draftId) {
        setIsSubmittingDraft(true)

        try {
          const urlParams = new URLSearchParams(location.search)
          const continueParam = urlParams.get('continue')

          if (continueParam === 'true') {
            console.log('Continue mode detected, unpublishing property first...')
            const unpublishResult = await unpublishProperty(draftId)
            if (!unpublishResult) {
              console.error('Failed to unpublish property')
              message.error('Failed to unpublish property. Please try again.')
              setIsSubmittingDraft(false)
              return
            }
            console.log('Property unpublished successfully')
          }

          const step4Saved = await saveStep4(draftId)
          if (!step4Saved) {
            console.error('Failed to save step 4')
            message.error('Failed to save step 4. Please try again.')
            setIsSubmittingDraft(false)
            return
          }

          const publishResult = await publishProperty(draftId)
          if (!publishResult) {
            console.error('Failed to publish property')
            message.error('Failed to publish property. Please try again.')
            setIsSubmittingDraft(false)
            return
          }

          console.log('Property published with documents:', publishResult)
          MixpanelTracker.track('Property Submission Completed', {
            'Property Type': formData.propertyType,
            'Monthly Price': formData.monthlyPrice,
            'Security Deposit': formData.securityDeposit,
            'Use HAUP Agent': formData.useHaupAgent,
            'Completed Step 4': true,
            'Property ID': publishResult.id,
            'Published At': publishResult.published_at,
            'Has Documents': publishResult.has_documents,
            'Continue Mode': continueParam === 'true',
          })

          history.push(`/property-submission/success?has_documents=${publishResult.has_documents}`)
        } catch (error) {
          console.error('Error in step 4 completion:', error)
          message.error('An error occurred while saving and publishing. Please try again.')
        } finally {
          setIsSubmittingDraft(false)
        }
      } else {
        message.error('Draft ID is missing. Please try again.')
      }
    } else {
      console.log('Form data:', formData)
      MixpanelTracker.track('Property Submission Completed', {
        'Property Type': formData.propertyType,
        'Monthly Price': formData.monthlyPrice,
        'Security Deposit': formData.securityDeposit,
        'Use HAUP Agent': formData.useHaupAgent,
      })
      history.push('/property-submission/success')
    }
  }, [
    currentStep,
    history,
    formData,
    location.pathname,
    location.search,
    draftId,
    createDraft,
    saveStep1,
    saveStep2,
    saveStep3,
    saveStep4,
    publishProperty,
    unpublishProperty,
    isSubmittingDraft,
  ])

  const handleSkipAndSubmit = useCallback(async () => {
    setShowCompletionModal(false)

    if (!draftId) {
      message.error('Draft ID is missing. Please try again.')
      return
    }

    setIsPublishing(true)

    try {
      const publishResult = await publishProperty(draftId)
      if (!publishResult) {
        message.error('Failed to publish property. Please try again.')
        setIsPublishing(false)
        return
      }

      console.log('Property published:', publishResult)
      MixpanelTracker.track('Property Submission Completed', {
        'Property Type': formData.propertyType,
        'Monthly Price': formData.monthlyPrice,
        'Security Deposit': formData.securityDeposit,
        'Use HAUP Agent': formData.useHaupAgent,
        'Skipped Step 4': true,
        'Property ID': publishResult.id,
        'Published At': publishResult.published_at,
        'Has Documents': publishResult.has_documents,
      })

      history.push(`/property-submission/success?has_documents=${publishResult.has_documents}`)
    } catch (error) {
      console.error('Error publishing property:', error)
      message.error('An error occurred while publishing. Please try again.')
    } finally {
      setIsPublishing(false)
    }
  }, [draftId, formData, history, publishProperty])

  const handleContinueToStep4 = useCallback(() => {
    setShowCompletionModal(false)
    history.push(`/property-submission/step4?draft_id=${draftId}`)
  }, [history, draftId])

  const handleSaveAndExit = useCallback(() => {
    Modal.confirm({
      title: 'Do you want to close this page?',
      content: 'Your information will be saved. You can return to it later.',
      okText: 'Save and exit',
      cancelText: 'Cancel',
      okButtonProps: {
        style: {
          backgroundColor: '#4C4848',
          borderColor: '#4C4848',
          color: 'white',
          fontFamily: 'Poppins, sans-serif',
          fontWeight: '500',
          boxShadow: '0 2px 8px rgba(183, 135, 90, 0.3)',
        },
      },
      cancelButtonProps: {
        style: {
          backgroundColor: 'transparent',
          borderColor: '#4C4848',
          color: '#4C4848',
          fontFamily: 'Poppins, sans-serif',
          fontWeight: '500',
        },
      },
      onOk() {
        MixpanelTracker.track('Property Submission Save and Exit', {
          'Current Step': currentStep,
        })
        history.push('/submission#property')
      },
      onCancel() {
        return
      },
    })
  }, [history, currentStep])

  const pathSegments = location.pathname.split('/')
  const stepSegment = pathSegments.find((segment) => segment.startsWith('step'))
  const isStep1_1 = stepSegment === 'step1-1'
  const isStep1_2 = stepSegment === 'step1-2'
  const shouldShowTutorial = !tutorialShown[currentStep] && !isStep1_1 && !isStep1_2
  const isSuccessPage = location.pathname.includes('/success')

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundImage: `url('/static/media/loginbg.9f35eb74.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        backgroundColor: '#F9FAFB',
      }}
    >
      {!shouldShowTutorial && !isSuccessPage && (
        <div
          style={{
            width: '100%',
            maxWidth: '500px',
            position: 'sticky',
            top: '0',
            zIndex: 10,
            backgroundColor: '#F9FAFB',
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingTop: '16px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: currentStep === 1 ? 'flex-end' : 'space-between',
              marginBottom: '20px',
            }}
          >
            {currentStep !== 1 && (
              <button
                onClick={handleSaveAndExit}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  color: '#212427',
                  border: '1px solid #D6DAE0',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  fontFamily: 'Poppins, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                Save and exit
              </button>
            )}
          </div>
          <Stepper
            currentStep={currentStep}
            totalSteps={4}
            stepProgress={stepProgress}
            onStepClick={handleStepClick}
          />
        </div>
      )}

      <div style={{ width: '100%', maxWidth: '500px', position: 'relative' }}>
        {isLoadingProperty && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 10,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '8px',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #4C4848',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px',
                }}
              />
              <p style={{ color: '#667085', fontFamily: 'Poppins, sans-serif' }}>Loading...</p>
              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}
              </style>
            </div>
          </div>
        )}
        <div style={{ opacity: isLoadingProperty ? 0.5 : 1 }}>
          <Switch>
            <Route exact path={`${path}/step1`}>
              {shouldShowTutorial ? (
                <StepTutorial
                  step={1}
                  title={tutorials[1].title}
                  description={tutorials[1].description}
                  icon={tutorials[1].icon}
                  onNext={() => handleTutorialNext(1)}
                />
              ) : (
                <Step1
                  onNext={handleNext}
                  onPrevious={handlePrevious}
                  onStepProgress={handleStep1Progress}
                  formData={formData}
                  updateFormData={updateFormData}
                />
              )}
            </Route>

            <Route exact path={`${path}/step1-1`}>
              <Step1_1
                onNext={handleNext}
                onPrevious={handlePrevious}
                onStepProgress={handleStep1_1Progress}
                formData={formData}
                updateFormData={updateFormData}
              />
            </Route>

            <Route exact path={`${path}/step1-2`}>
              <Step1_2
                onNext={handleNext}
                onPrevious={handlePrevious}
                onStepProgress={handleStep1_2Progress}
                formData={formData}
                updateFormData={updateFormData}
                isSubmittingDraft={isSubmittingDraft}
              />
            </Route>

            <Route exact path={`${path}/step2`}>
              {shouldShowTutorial ? (
                <StepTutorial
                  step={2}
                  title={tutorials[2].title}
                  description={tutorials[2].description}
                  icon={tutorials[2].icon}
                  onNext={() => handleTutorialNext(2)}
                  onBack={() => handleTutorialBack(2)}
                />
              ) : (
                <Step2
                  onNext={handleNext}
                  onPrevious={handlePrevious}
                  onStepProgress={handleStep2Progress}
                  formData={formData}
                  updateFormData={updateFormData}
                  isSubmittingDraft={isSubmittingDraft}
                />
              )}
            </Route>

            <Route exact path={`${path}/step3`}>
              {shouldShowTutorial ? (
                <StepTutorial
                  step={3}
                  title={tutorials[3].title}
                  description={tutorials[3].description}
                  icon={tutorials[3].icon}
                  onNext={() => handleTutorialNext(3)}
                  onBack={() => handleTutorialBack(3)}
                />
              ) : (
                <Step3
                  onNext={handleNext}
                  onPrevious={handlePrevious}
                  onStepProgress={handleStep3Progress}
                  formData={formData}
                  updateFormData={updateFormData}
                  isSubmittingDraft={isSubmittingDraft}
                />
              )}
            </Route>

            <Route exact path={`${path}/step4`}>
              {shouldShowTutorial ? (
                <StepTutorial
                  step={4}
                  title={tutorials[4].title}
                  description={tutorials[4].description}
                  icon={tutorials[4].icon}
                  onNext={() => handleTutorialNext(4)}
                  onBack={() => handleTutorialBack(4)}
                />
              ) : (
                <Step4
                  onNext={handleNext}
                  onPrevious={handlePrevious}
                  onStepProgress={handleStep4Progress}
                  formData={formData}
                  updateFormData={updateFormData}
                  isSubmittingDraft={isSubmittingDraft}
                />
              )}
            </Route>

            <Route exact path={`${path}/success`}>
              <PropertySubmissionSuccess />
            </Route>

            <Route exact path={path}>
              {shouldShowTutorial ? (
                <StepTutorial
                  step={1}
                  title={tutorials[1].title}
                  description={tutorials[1].description}
                  icon={tutorials[1].icon}
                  onNext={() => handleTutorialNext(1)}
                />
              ) : (
                <Step1
                  onNext={handleNext}
                  onPrevious={handlePrevious}
                  onStepProgress={handleStep1Progress}
                  formData={formData}
                  updateFormData={updateFormData}
                />
              )}
            </Route>
          </Switch>
        </div>
      </div>

      <Modal
        open={showCompletionModal}
        onCancel={() => setShowCompletionModal(false)}
        footer={null}
        width={490}
        maskClosable={false}
        centered
        bodyStyle={{
          padding: '32px 24px 24px 24px',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '24px' }}>
            <img
              src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/4E8wfpLxbfk4CZWLgRZnzmh25xzfYi5h99N7gPAn3oAcpe8smA14DfUKLJMCkXktFzh9mzR5jXAP6g3t16yHootj.png"
              alt="Success"
              style={{ width: '120px', height: '120px' }}
            />
          </div>

          <div
            style={{
              backgroundColor: '#F9FAFB',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              textAlign: 'center',
            }}
          >
            <h2
              style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#4C4848',
                marginBottom: '12px',
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              You've completed the main steps!
            </h2>

            <p
              style={{
                fontSize: '14px',
                color: '#667085',
                lineHeight: '1.5',
                marginBottom: '24px',
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              You've completed all the required steps. Your property is now listed on the platform
              and ready for bookings.
            </p>

            <div style={{ textAlign: 'left' }}>
              {[
                'Tell us about your property',
                'Make your property stand out',
                'Set your price and earnings',
              ].map((step, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px',
                  }}
                >
                  <img
                    src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/3HXxGXbyeasfDnr3ewK3Pv4mtpeg4Dr6bzyryscaxQ63ztw7LZfzNbdgJbyNZZxQwXyvmLtpt5yChUNWye89oJqw.png"
                    alt="Check"
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span
                    style={{ fontSize: '16px', color: '#333', fontFamily: 'Poppins, sans-serif' }}
                  >
                    {step}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '32px', textAlign: 'left' }}>
            <h3
              style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#333',
                marginBottom: '12px',
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              Completed this step anytime
            </h3>
            <p
              style={{
                fontSize: '14px',
                color: '#667085',
                marginBottom: '16px',
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              You can skip these for now, but finishing them will help boost your listing's
              visibility and trust.
            </p>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div
                style={{ fontSize: '18px', fontWeight: 'bold', color: '#000', minWidth: '24px' }}
              >
                4.
              </div>
              <div style={{ flex: 1 }}>
                <span
                  style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#333',
                    fontFamily: 'Poppins, sans-serif',
                    display: 'block',
                    marginBottom: '8px',
                  }}
                >
                  Upload documents
                </span>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <p
                    style={{
                      fontSize: '14px',
                      color: '#667085',
                      margin: '0',
                      fontFamily: 'Poppins, sans-serif',
                      flex: 1,
                    }}
                  >
                    Upload the required documents to verify your property to build trust and ensures
                    a smooth rental process.
                  </p>
                  <img
                    src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/1KyatujG6VKojwLvasUqSvfBWyRyVYwycumkpLv5q7DaqKKqWj5qhxJBictMUPvUD4sePsYnhzFH9xQ3heLYKLzk.png"
                    alt="Upload documents"
                    style={{ width: '60px', height: '60px', flexShrink: 0 }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleSkipAndSubmit}
              disabled={isPublishing}
              className="completion-modal-button"
              style={{
                flex: '1',
                backgroundColor: 'transparent',
                color: isPublishing ? '#ccc' : '#4C4848',
                border: `1px solid ${isPublishing ? '#ccc' : '#4C4848'}`,
                borderRadius: '24px',
                cursor: isPublishing ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                fontSize: '16px',
                fontFamily: 'Poppins, sans-serif',
                transition: 'all 0.2s ease',
                opacity: isPublishing ? 0.6 : 1,
              }}
            >
              {isPublishing ? 'Publishing...' : 'Skip and submit'}
            </button>
            <button
              onClick={handleContinueToStep4}
              disabled={isPublishing}
              className="completion-modal-button"
              style={{
                flex: '1',
                backgroundColor: isPublishing ? '#ccc' : '#4C4848',
                color: 'white',
                border: 'none',
                borderRadius: '24px',
                cursor: isPublishing ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                fontSize: '16px',
                fontFamily: 'Poppins, sans-serif',
                transition: 'all 0.2s ease',
                boxShadow: isPublishing ? 'none' : '0 2px 8px rgba(183, 135, 90, 0.3)',
                opacity: isPublishing ? 0.6 : 1,
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default PropertySubmissionPage
